import React, { useState, useRef } from "react";
import {
  Card,
  Button,
  Form,
  Alert,
  ProgressBar,
  Row,
  Col,
  Modal,
} from "react-bootstrap";
import {
  ref,
  uploadBytesResumable,
  getDownloadURL,
  deleteObject,
} from "firebase/storage";
import JSZip from "jszip";
import imageCompression from "browser-image-compression"; // Import compression
import { storage } from "../../firebase";

const PhotoUpload = ({
  propertyId,
  photos = [],
  onPhotosUpdate,
  disabled = false,
  previewHeight = "120px",
  colProps = { xs: 6, md: 4, lg: 3 },
}) => {
  // --- State ---
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [error, setError] = useState("");

  // Modal & Selection State
  const [showModal, setShowModal] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState(null);
  const [isSelectMode, setIsSelectMode] = useState(false);
  const [selectedPaths, setSelectedPaths] = useState(new Set());

  // Drag State
  const [draggedIndex, setDraggedIndex] = useState(null);

  // --- Refs ---
  const fileInputRef = useRef(null);
  const zipInputRef = useRef(null);
  const activeTasksRef = useRef([]);
  const isCancelledRef = useRef(false); // <--- ADD THIS

  // --- Helpers ---
  const validateFile = (file) => {
    if (
      !file.type.startsWith("image/") &&
      !file.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    ) {
      throw new Error(`File ${file.name} is not a valid image`);
    }
    // Hard limit for original file before compression attempts
    const maxSize = 30 * 1024 * 1024; // 30MB
    if (file.size > maxSize) {
      throw new Error(`File ${file.name} is too large (Max 30MB)`);
    }
    return true;
  };

  // --- Upload Logic (Updated with Compression) ---
  const uploadSinglePhoto = async (file) => {
    validateFile(file);

    // 1. COMPRESSION STEP
    let fileToUpload = file;
    try {
      // Compression options suitable for Real Estate (High quality, manageable size)
      const options = {
        maxSizeMB: 1, // Compress to approx 1MB
        maxWidthOrHeight: 1920, // Resize to HD standard
        useWebWorker: true, // Use separate thread to avoid freezing UI
        fileType: "image/jpeg", // Force convert to JPEG
      };

      // Only compress if larger than 1MB
      if (file.size > 1024 * 1024) {
        // console.log(`Compressing ${file.name}...`);
        fileToUpload = await imageCompression(file, options);
        // console.log(`Compressed ${file.name}: ${(file.size/1024/1024).toFixed(2)}MB -> ${(fileToUpload.size/1024/1024).toFixed(2)}MB`);
      }
    } catch (err) {
      console.warn("Image compression failed, uploading original file:", err);
      // Fallback: fileToUpload remains the original 'file'
    }

    // 2. FILENAME & PATH
    const timestamp = Date.now();
    const safeName = file.name.replace(/[^a-zA-Z0-9.-]/g, "_");
    const filename = `${timestamp}_${safeName}`;
    const photoPath = `properties/${propertyId}/photos/${filename}`;

    // 3. UPLOAD
    const storageRef = ref(storage, photoPath);
    const uploadTask = uploadBytesResumable(storageRef, fileToUpload);

    activeTasksRef.current.push(uploadTask);

    try {
      const snapshot = await uploadTask;
      const downloadURL = await getDownloadURL(snapshot.ref);

      return {
        url: downloadURL,
        path: photoPath,
        filename: filename,
        size: fileToUpload.size, // Save the compressed size
        type: fileToUpload.type,
        uploadedAt: new Date().toISOString(),
      };
    } finally {
      activeTasksRef.current = activeTasksRef.current.filter(
        (t) => t !== uploadTask,
      );
    }
  };

  const processUploadBatch = async (files) => {
    if (files.length === 0) return;

    // 1. Reset Cancellation State
    isCancelledRef.current = false; // Important: Reset this every time we start!

    setUploading(true);
    setError("");
    activeTasksRef.current = [];

    let completedCount = 0;

    setUploadProgress({
      current: 0,
      total: files.length,
      filename: "Preparing files...",
      percentage: 0,
    });

    const successfulPhotos = [];

    try {
      const CHUNK_SIZE = 3;

      for (let i = 0; i < files.length; i += CHUNK_SIZE) {
        // --- CRITICAL CHECK: Stop if user clicked Cancel ---
        if (isCancelledRef.current) break;

        // Get the next batch of 3 files
        const chunk = files.slice(i, i + CHUNK_SIZE);

        const batchNum = Math.floor(i / CHUNK_SIZE) + 1;
        const totalBatches = Math.ceil(files.length / CHUNK_SIZE);

        setUploadProgress((prev) => ({
          ...prev,
          filename: `Processing batch ${batchNum} of ${totalBatches}...`,
        }));

        const chunkPromises = chunk.map(async (file) => {
          if (isCancelledRef.current) return null; // Double check inside map

          try {
            const photo = await uploadSinglePhoto(file);

            completedCount++;
            setUploadProgress((prev) => {
              if (!prev || isCancelledRef.current) return prev;
              return {
                ...prev,
                current: completedCount,
                filename: `Uploaded ${completedCount} of ${files.length}`,
                percentage: (completedCount / files.length) * 100,
              };
            });
            return photo;
          } catch (err) {
            if (err.code === "storage/canceled") return null;
            console.error(`Failed to upload ${file.name}:`, err);
            setError((prev) =>
              prev ? `${prev}, Failed: ${file.name}` : `Failed: ${file.name}`,
            );
            return null;
          }
        });

        // Wait for this chunk to finish
        const chunkResults = await Promise.all(chunkPromises);

        if (isCancelledRef.current) break; // Stop before processing results if canceled

        const validResults = chunkResults.filter((p) => p !== null);
        successfulPhotos.push(...validResults);
      }

      // Only update state if we weren't cancelled (or if we want to save partial progress)
      if (!isCancelledRef.current && successfulPhotos.length > 0) {
        const updatedPhotos = [...photos, ...successfulPhotos];
        onPhotosUpdate(updatedPhotos);
      }
    } catch (err) {
      if (err.message !== "Upload canceled by user") {
        setError("Batch upload failed: " + err.message);
      }
    } finally {
      // Only turn off "Uploading" spinner if we are truly done
      // (This prevents flickering if the loop exited early)
      setUploading(false);
      setUploadProgress(null);
      activeTasksRef.current = [];
      if (fileInputRef.current) fileInputRef.current.value = "";
      if (zipInputRef.current) zipInputRef.current.value = "";
    }
  };

  // --- Handlers (Standard, Zip, Delete, etc.) ---

  const handleCancelUpload = () => {
    isCancelledRef.current = true; // <--- ADD THIS

    // Cancel all active Firebase tasks
    activeTasksRef.current.forEach((task) => task.cancel());
    activeTasksRef.current = [];

    setUploading(false);
    setUploadProgress(null);
    setError("Upload canceled by user.");
  };

  const handleFileSelect = (e) => {
    processUploadBatch(Array.from(e.target.files));
  };

  const handleZipSelect = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    setUploadProgress({
      current: 0,
      total: 0,
      filename: "Extracting ZIP...",
      percentage: 0,
    });

    try {
      const zip = new JSZip();
      const zipContents = await zip.loadAsync(file);
      const imageFiles = [];
      const entries = Object.values(zipContents.files);

      for (const entry of entries) {
        if (entry.dir) continue;
        if (entry.name.includes("__MACOSX") || entry.name.includes(".DS_Store"))
          continue;
        if (!entry.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)) continue;

        const blob = await entry.async("blob");
        const simpleName = entry.name.split("/").pop();
        const imageFile = new File([blob], simpleName, {
          type: blob.type || "image/jpeg",
        });
        imageFiles.push(imageFile);
      }

      if (imageFiles.length === 0)
        throw new Error("No valid images found in this ZIP file.");
      await processUploadBatch(imageFiles);
    } catch (err) {
      console.error("Zip Error", err);
      setError("Failed to process ZIP: " + err.message);
      setUploading(false);
      setUploadProgress(null);
      if (zipInputRef.current) zipInputRef.current.value = "";
    }
  };

  const toggleSelectMode = () => {
    setIsSelectMode(!isSelectMode);
    setSelectedPaths(new Set());
  };

  const handleSelectAll = () => {
    if (selectedPaths.size === photos.length) {
      setSelectedPaths(new Set());
    } else {
      const allPaths = new Set(photos.map((p) => p.path));
      setSelectedPaths(allPaths);
    }
  };

  const togglePhotoSelection = (path) => {
    const newSelection = new Set(selectedPaths);
    if (newSelection.has(path)) {
      newSelection.delete(path);
    } else {
      newSelection.add(path);
    }
    setSelectedPaths(newSelection);
  };

  const handleBulkDelete = async () => {
    if (selectedPaths.size === 0) return;
    if (!window.confirm(`Delete ${selectedPaths.size} photos?`)) return;

    setUploading(true);
    setError("");

    try {
      // Create promises but handle 404s individually
      const deletePromises = Array.from(selectedPaths).map(async (path) => {
        const photoRef = ref(storage, path);
        try {
          await deleteObject(photoRef);
        } catch (err) {
          if (err.code === "storage/object-not-found") {
            return; // Ignore missing files
          }
          throw err; // Stop on other errors
        }
      });

      // Wait for all attempts
      await Promise.allSettled(deletePromises);

      // Always remove selected paths from the UI, even if files were missing
      const remainingPhotos = photos.filter((p) => !selectedPaths.has(p.path));
      onPhotosUpdate(remainingPhotos);

      setIsSelectMode(false);
      setSelectedPaths(new Set());
    } catch (err) {
      setError("Bulk delete failed: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeletePhoto = async (photo) => {
    if (!window.confirm("Are you sure you want to delete this photo?")) return;
    try {
      setUploading(true);
      const photoRef = ref(storage, photo.path);

      try {
        await deleteObject(photoRef);
      } catch (innerErr) {
        // If file is already gone (404), we still want to remove it from the UI/DB
        if (innerErr.code === "storage/object-not-found") {
          console.warn(
            "File was already missing from storage. Removing from DB...",
          );
        } else {
          throw innerErr; // Re-throw real errors (like permission issues)
        }
      }

      // Proceed to update UI/DB regardless of whether storage delete worked
      const updatedPhotos = photos.filter((p) => p.path !== photo.path);
      onPhotosUpdate(updatedPhotos);
      setShowModal(false);
      setSelectedPhoto(null);
    } catch (err) {
      setError("Failed to delete photo: " + err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSetMainPhoto = (photo) => {
    const updatedPhotos = [
      photo,
      ...photos.filter((p) => p.path !== photo.path),
    ];
    onPhotosUpdate(updatedPhotos);
    setShowModal(false);
    setSelectedPhoto(null);
  };

  const handleMovePhoto = (index, direction) => {
    const newPhotos = [...photos];
    if (direction === "prev") {
      if (index === 0) return;
      [newPhotos[index - 1], newPhotos[index]] = [
        newPhotos[index],
        newPhotos[index - 1],
      ];
    } else {
      if (index === newPhotos.length - 1) return;
      [newPhotos[index + 1], newPhotos[index]] = [
        newPhotos[index],
        newPhotos[index + 1],
      ];
    }
    onPhotosUpdate(newPhotos);
  };

  const handleDragStart = (e, index) => {
    if (isSelectMode) return;
    setDraggedIndex(index);
    e.dataTransfer.effectAllowed = "move";
  };
  const handleDragOver = (e, index) => {
    if (isSelectMode) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };
  const handleDrop = (e, dropIndex) => {
    if (isSelectMode) return;
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === dropIndex) return;
    const newPhotos = [...photos];
    const draggedPhoto = newPhotos[draggedIndex];
    newPhotos.splice(draggedIndex, 1);
    newPhotos.splice(dropIndex, 0, draggedPhoto);
    onPhotosUpdate(newPhotos);
    setDraggedIndex(null);
  };

  const openPhotoModal = (photo) => {
    if (isSelectMode) {
      togglePhotoSelection(photo.path);
    } else {
      setSelectedPhoto(photo);
      setShowModal(true);
    }
  };

  return (
    <>
      <Card className="mb-3">
        <Card.Header className="d-flex justify-content-between align-items-center">
          <h6 className="mb-0">
            {isSelectMode
              ? `Selected (${selectedPaths.size})`
              : "Property Photos"}
          </h6>
          <div className="d-flex gap-2">
            {isSelectMode ? (
              <>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={handleSelectAll}
                  disabled={uploading}
                >
                  {selectedPaths.size === photos.length
                    ? "Deselect All"
                    : "Select All"}
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleBulkDelete}
                  disabled={selectedPaths.size === 0 || uploading}
                >
                  {uploading ? "Deleting..." : "Delete Selected"}
                </Button>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={toggleSelectMode}
                  disabled={uploading}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={toggleSelectMode}
                  disabled={disabled || uploading || photos.length === 0}
                >
                  Select / Delete
                </Button>
                <Button
                  variant="outline-secondary"
                  size="sm"
                  onClick={() => zipInputRef.current?.click()}
                  disabled={disabled || uploading}
                >
                  {uploading ? "Wait..." : "Upload ZIP"}
                </Button>
                <Button
                  variant="outline-primary"
                  size="sm"
                  onClick={() => fileInputRef.current?.click()}
                  disabled={disabled || uploading}
                >
                  {uploading ? "Uploading..." : "+ Add Photos"}
                </Button>
              </>
            )}
          </div>
        </Card.Header>

        <Card.Body>
          <Form.Control
            ref={fileInputRef}
            type="file"
            multiple
            accept="image/*"
            onChange={handleFileSelect}
            style={{ display: "none" }}
          />
          <Form.Control
            ref={zipInputRef}
            type="file"
            accept=".zip,application/zip"
            onChange={handleZipSelect}
            style={{ display: "none" }}
          />

          {error && (
            <Alert variant="danger" dismissible onClose={() => setError("")}>
              {error}
            </Alert>
          )}

          {uploading && uploadProgress && (
            <Alert variant="info" className="mb-3">
              <div className="d-flex justify-content-between align-items-center mb-2">
                <span>{uploadProgress.filename}</span>
                <Button
                  variant="danger"
                  size="sm"
                  onClick={handleCancelUpload}
                  style={{
                    lineHeight: 1,
                    padding: "2px 8px",
                    fontSize: "12px",
                  }}
                >
                  Cancel Upload
                </Button>
              </div>
              <ProgressBar animated now={uploadProgress.percentage} />
            </Alert>
          )}

          {photos.length === 0 ? (
            <div className="text-center py-4 text-muted">
              <p>No photos uploaded yet.</p>
              <small>
                Click "Add Photos" or "Upload ZIP" to upload images.
              </small>
            </div>
          ) : (
            <Row>
              {photos.map((photo, index) => {
                const isSelected = selectedPaths.has(photo.path);
                return (
                  <Col
                    key={photo.path || index}
                    {...colProps}
                    className="mb-3"
                    draggable={!disabled && !uploading && !isSelectMode}
                    onDragStart={(e) => handleDragStart(e, index)}
                    onDragOver={(e) => handleDragOver(e, index)}
                    onDrop={(e) => handleDrop(e, index)}
                    onDragEnd={() => setDraggedIndex(null)}
                    style={{
                      opacity: draggedIndex === index ? 0.4 : 1,
                      transition: "all 0.2s ease",
                      cursor: isSelectMode
                        ? "pointer"
                        : uploading
                          ? "wait"
                          : "grab",
                    }}
                  >
                    <div
                      className={`position-relative group border rounded overflow-hidden shadow-sm h-100 ${isSelected ? "border-primary border-3" : ""}`}
                      onClick={() =>
                        isSelectMode && togglePhotoSelection(photo.path)
                      }
                    >
                      <img
                        src={photo.url}
                        alt={`Photo ${index}`}
                        className="img-fluid"
                        style={{
                          width: "100%",
                          height: previewHeight,
                          objectFit: "cover",
                          pointerEvents: "none",
                          opacity: isSelectMode && !isSelected ? 0.6 : 1,
                        }}
                      />
                      {isSelectMode && (
                        <div className="position-absolute top-0 end-0 p-2">
                          <div
                            className={`rounded-circle border ${isSelected ? "bg-primary border-primary" : "bg-white border-secondary"}`}
                            style={{
                              width: "20px",
                              height: "20px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                            }}
                          >
                            {isSelected && (
                              <span
                                className="text-white"
                                style={{ fontSize: "12px" }}
                              >
                                ✓
                              </span>
                            )}
                          </div>
                        </div>
                      )}
                      {!isSelectMode && index === 0 && (
                        <div
                          className="position-absolute top-0 start-0 bg-primary text-white px-2 py-1 rounded-bottom-end"
                          style={{ fontSize: "0.75rem" }}
                        >
                          Main
                        </div>
                      )}
                      <div
                        className="position-absolute top-0 start-0 w-100 h-100"
                        onClick={(e) => {
                          e.stopPropagation();
                          openPhotoModal(photo);
                        }}
                        style={{ zIndex: 5 }}
                      ></div>

                      {!isSelectMode && (
                        <div
                          className="position-absolute bottom-0 start-0 end-0 p-1 d-flex justify-content-between bg-dark bg-opacity-75"
                          style={{ zIndex: 20 }}
                        >
                          <Button
                            variant="link"
                            className="text-white p-0 px-2 text-decoration-none"
                            disabled={index === 0}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMovePhoto(index, "prev");
                            }}
                          >
                            ←
                          </Button>
                          <span
                            className="text-white text-opacity-50"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {index + 1}
                          </span>
                          <Button
                            variant="link"
                            className="text-white p-0 px-2 text-decoration-none"
                            disabled={index === photos.length - 1}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleMovePhoto(index, "next");
                            }}
                          >
                            →
                          </Button>
                        </div>
                      )}
                    </div>
                  </Col>
                );
              })}
            </Row>
          )}
        </Card.Body>
      </Card>

      <Modal
        show={showModal}
        onHide={() => setShowModal(false)}
        size="lg"
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Photo Options</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {selectedPhoto && (
            <div className="text-center">
              <img
                src={selectedPhoto.url}
                alt="Detail"
                className="img-fluid rounded mb-3"
                style={{ maxHeight: "400px" }}
              />
              <div className="mb-3">
                <strong>Filename:</strong> {selectedPhoto.filename}
                <br />
                <strong>Size:</strong>{" "}
                {(selectedPhoto.size / 1024 / 1024).toFixed(2)} MB
              </div>
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowModal(false)}>
            Close
          </Button>
          {selectedPhoto && photos.indexOf(selectedPhoto) !== 0 && (
            <Button
              variant="primary"
              onClick={() => handleSetMainPhoto(selectedPhoto)}
              disabled={uploading}
            >
              Set as Main Photo
            </Button>
          )}
          <Button
            variant="danger"
            onClick={() => handleDeletePhoto(selectedPhoto)}
            disabled={uploading}
          >
            Delete Photo
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default PhotoUpload;
